// ReportingBlock.jsx (child)
import * as React from "react";
import {
    Box, Stack, Typography, Chip, Divider, Button, Paper
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import BugReportIcon from "@mui/icons-material/BugReport";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";

function Title({ children, ...props }) {
    return (
        <Typography component="h2" variant="h6" color="primary" gutterBottom {...props}>
            {children}
        </Typography>
    );
}

function ReasonRow({ label, chips = [], serious = false }) {
    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <ReportProblemIcon sx={{ color: serious ? "error.main" : "warning.main" }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Box sx={{ fontWeight: 500 }}>{label}</Box>
                {chips.map((c, i) => (
                    <Chip key={`${c}-${i}`} label={c} size="small" />
                ))}
            </Stack>
        </Stack>
    );
}

function ReasonsSection({ report, linksInContent, visualInContent,
                        titleContent,
                        descriptionContent,isSerious }) {
    const brandMatches = report?.brand_matches ?? [];
    const hasAudioKeyword = (report?.report_entries ?? []).some((p) => !p.visual_only && !p.matched_from_ocr);
    const hasOnScreenTextKeyword = (report?.report_entries ?? []).some((p) => !p.visual_only && p.matched_from_ocr);

    const reasons = [
        brandMatches.length
            ? { id: "brand_refs", label: "References", chips: brandMatches.map(b => b.brand_name) }
            : null,
        linksInContent ? { id: "links", label: "Links to your brand" } : null,
        visualInContent ? { id: "visual", label: "Contains a visual representation of a brand" } : null,
        hasAudioKeyword ? { id: "audio", label: "Audio contains a target keyword" } : null,
        hasOnScreenTextKeyword ? { id: "ocr", label: "A keyword matched with on-screen text" } : null,
        titleContent ? { id: "title", label: "References a brand in the video title" } : null,
        descriptionContent ? { id: "description", label: "References a brand in the description" } : null,
    ].filter(Boolean);

    if (!reasons.length) {
        return <Typography variant="body2" color="text.secondary">No specific reporting reasons detected.</Typography>;
    }

    return (
        <Stack spacing={1}>
            {reasons.map(r => (
                <ReasonRow key={r.id} label={r.label} chips={r.chips} serious={isSerious} />
            ))}
        </Stack>
    );
}

export default function ReportingBlock({
                                           report,
                                           linksInContent,
                                           visualInContent,
                                           titleContent,
                                           descriptionContent,
                                           isSerious,
                                           readonly,
                                           onNewIssue,
                                           onAssignExisting,
                                           onNoIssue,
                                       }) {
    const actions = [
        {
            id: "new",
            label: "Create New Issue",
            short: "Start investigation",
            description: "Open a new issue when this looks like a fresh case that needs investigation.",
            icon: <BugReportIcon />,
            cardBg: "#fff7ed",
            borderColor: "#fdba74",
            buttonColor: "warning",
            onClick: onNewIssue
        },
        {
            id: "assign",
            label: "Assign Existing Issue",
            short: "Link to known case",
            description: "Attach this report to an existing issue if it matches a case already being tracked.",
            icon: <AssignmentIcon />,
            cardBg: "#fff7ed",
            borderColor: "#fdba74",
            buttonColor: "warning",
            onClick: onAssignExisting
        },
        {
            id: "none",
            label: "Mark No Issue",
            short: "Close this review",
            description: "Record that no investigation is required after reviewing the evidence.",
            icon: <DoNotTouchIcon />,
            cardBg: "#f8fafc",
            borderColor: "#cbd5e1",
            buttonColor: "inherit",
            onClick: onNoIssue
        },
    ];

    return (
        <Box sx={{ p: 1, borderBottom: 1, borderColor: "divider" }}>
            <Title sx={{ mb: 1 }}>Reporting reasons</Title>
            <ReasonsSection
                report={report}
                linksInContent={linksInContent}
                visualInContent={visualInContent}
                titleContent={titleContent}
                descriptionContent={descriptionContent}
                isSerious={isSerious}
            />

            {!readonly && (
                <>
                    <Divider sx={{ my: 1.5 }} />
                    <Title sx={{ mb: 0.5 }}>Decide Next Action</Title>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Review the reasons above, then choose how this report should be handled.
                    </Typography>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                            gap: 1
                        }}
                    >
                        {actions.map((action) => {
                            return (
                                <Paper
                                    key={action.id}
                                    variant="outlined"
                                    sx={{
                                        p: 1.25,
                                        borderColor: action.borderColor,
                                        bgcolor: action.cardBg
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                        {action.icon}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {action.short}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                                        {action.description}
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color={action.buttonColor}
                                        onClick={action.onClick}
                                    >
                                        {action.label}
                                    </Button>
                                </Paper>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
}
